import { Badge, Button, buttonClass, cardClass, fieldClass, selectClass, Table, textareaClass } from '@ledgerloom/ui';
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, type Outcome } from '../api';
import { useApi, usePageTitle } from '../hooks';
import { hasSurface, isAdmin, useSession } from '../session';
import { Breadcrumb, Empty, ErrorBanner, Icon, ListSkeleton, Loading, outcomeMessage, outcomeTone, Time, ToggleGroup, useToast, badgeTone } from '../components/ui';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { DesignerRequests } from '../components/DesignerRequests';
import { human, jobName } from '../format';
import { STATUS_LABEL, type AgentRow } from './Agents';

// How agents are organized and how they work together (ADR 0020): groups,
// workflows, signals, consults with their rulings, and the trace of each chain
// of work. Every change here is a change set or a proposal a person signs.
// Patterns: navigation/navigation-menu for the section links, data-display/table
// for groups and signals, data-display/list-view for workflows and threads,
// content-management/modal for each change, data-display/timeline for a trace
// and a thread, social/activity-feed for a feed. Text agents wrote (notes,
// questions, rationales) is rendered as text, never as markup.

// What the less obvious sections show: the page's intro.
const SECTION_HELP = {
  signals: 'Things agents noticed that may need action, such as a deal gone quiet. Agents that follow that kind of thing are told.',
  consults: 'Questions an agent asks whoever is in charge of an area, and work one agent hands to another.',
  traces: 'The whole story of one piece of work: what started it and everything it led to.',
};

const SECTIONS: Array<[string, string]> = [
  ['/agents', 'Hired agents'], ['/agents/groups', 'Groups'], ['/agents/workflows', 'Workflows'],
  ['/agents/signals', 'Signals'], ['/agents/consults', 'Consults'], ['/agents/traces', 'Traces'],
];

// A section's own page starts a trail back to where the person came from (the Agent Desk
// by default); moving between sections replaces the page, so the trail keeps that origin.
export function AgentWorkNav() {
  const { pathname } = useLocation();
  const current = SECTIONS.filter(([to]) => pathname === to || (to !== '/agents' && pathname.startsWith(`${to}/`)))[0]?.[0]
    ?? (pathname.startsWith('/agents/') && !SECTIONS.some(([to]) => to !== '/agents' && pathname.startsWith(to)) ? '/agents' : null);
  const section = SECTIONS.find(([to]) => to === pathname);
  return (
    <>
      <nav className="subnav" aria-label="Agent work">
        {SECTIONS.map(([to, label]) => <span key={to} className="subnav-item"><Link to={to} replace={!!section} aria-current={current === to ? 'page' : undefined}>{label}</Link></span>)}
      </nav>
      {section && <Breadcrumb items={[{ to: '/desk', label: 'Agent Desk' }, { label: section[0] === '/agents' ? 'Agents' : section[1] }]} />}
    </>
  );
}

const canConfigure = (role: string) => ['owner', 'admin', 'manager'].includes(role);

// "agent:x@t" reads as the agent's name; a person reads as "A person".
function useNames() {
  const agents = useApi<AgentRow[]>('/v1/agents');
  const groups = useApi<GroupRow[]>('/v1/agent-groups');
  return useMemo(() => {
    const a = new Map((agents.data ?? []).map((x) => [x.id, x.name]));
    const g = new Map((groups.data ?? []).map((x) => [x.id, x.name]));
    return {
      agents: agents.data ?? [], groups: groups.data ?? [],
      agent: (id: string) => a.get(id) ?? id,
      target: (type: string, id: string) => (type === 'group' ? g.get(id) ?? 'A group' : a.get(id) ?? id),
      actor: (actor: string) => (actor.startsWith('agent:') ? a.get(actor.slice(6)) ?? actor.slice(6) : actor.startsWith('user:') ? 'A person' : actor.startsWith('system:') ? `System (${actor.slice(7)})` : 'The database'),
    };
  }, [agents.data, groups.data]);
}

// A change made on these screens is a proposal: say what happened to it.
function useSubmit(after: () => void) {
  const toast = useToast();
  const { refreshStats } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // `last` false keeps the dialog open for a request that follows this one.
  const run = async (path: string, body: unknown, saved: string, last = true) => {
    setBusy(true); setError(null);
    try {
      const out = await api<Outcome>(path, { body });
      toast(outcomeMessage(out, saved), outcomeTone(out));
      refreshStats();
      if (out.state !== 'rejected' && last) after();
      return out;
    } catch (e) { setError(e); return null; } finally { setBusy(false); }
  };
  return { busy, error, setError, run };
}

function Page({ title, intro, action, children }: { title: string; intro: ReactNode; action?: ReactNode; children: ReactNode }) {
  usePageTitle(title);
  return (
    <div className="stack">
      <AgentWorkNav />
      <div className="page-head"><div><h1>{title}</h1><p>{intro}</p></div>{action}</div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

interface GroupRow { id: string; name: string; purpose: string | null; owner_user_id: string; owner_name: string | null; status: string; created_at: string; members: number; queued: number; claimed: number; domains: string[] }

export function Groups() {
  const { me } = useSession();
  const groups = useApi<GroupRow[]>('/v1/agent-groups');
  const [creating, setCreating] = useState(false);
  return (
    <Page title="Groups" intro="Agents grouped by the work they share. Work sent to a group goes to one member that may take it; membership never widens what a member may do."
      action={canConfigure(me.role) && <Button type="submit" onClick={() => setCreating(true)}><Icon name="plus" />Create a group</Button>}>
      <ErrorBanner error={groups.error} />
      <section className={cardClass} aria-labelledby="groups-h">
        <div className="card-head"><h2 id="groups-h">Groups</h2></div>
        {groups.loading && !groups.data ? <ListSkeleton rows={3} label="Loading groups" /> : !groups.data?.length ? (
          <Empty title="No groups yet">Group agents that share work, then subscribe the group to a topic or hand work to it.</Empty>
        ) : (
          <Table label="Agent groups">
              <caption className="sr-only">Agent groups</caption>
              <thead><tr><th scope="col">Group</th><th scope="col">Members</th><th scope="col">Queue</th><th scope="col" className="hide-sm">Rules on</th><th scope="col" className="hide-sm">Owner</th></tr></thead>
              <tbody>
                {groups.data.map((g) => (
                  <tr key={g.id}>
                    <td><Link to={`/agents/groups/${g.id}`}>{g.name}</Link>{g.status === 'archived' && <Badge style={{ marginLeft: 6 }}>Archived</Badge>}{g.purpose && <div className="faint">{g.purpose}</div>}</td>
                    <td>{g.members}</td>
                    <td className="small">{g.queued} waiting · {g.claimed} in hand</td>
                    <td className="small hide-sm">{g.domains.length ? g.domains.join(', ') : <span className="faint">Nothing</span>}</td>
                    <td className="small hide-sm">{g.owner_name ?? '–'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
        )}
      </section>
      {creating && <CreateGroup onClose={() => setCreating(false)} onDone={() => { setCreating(false); void groups.reload(); }} />}
    </Page>
  );
}

function AgentPicker({ agents, chosen, onChange, legend }: { agents: AgentRow[]; chosen: string[]; onChange: (v: string[]) => void; legend: string }) {
  return (
    <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
      <legend>{legend}</legend>
      <div className="stack" style={{ gap: 4, maxHeight: 220, overflow: 'auto' }}>
        {agents.filter((a) => a.status !== 'retired').map((a) => (
          <label key={a.id} className="row small">
            <input type="checkbox" checked={chosen.includes(a.id)} onChange={(e) => onChange(e.target.checked ? [...chosen, a.id] : chosen.filter((x) => x !== a.id))} />
            {a.name} <span className="faint">({human(a.class)}, {STATUS_LABEL[a.status] ?? human(a.status)})</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function CreateGroup({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const names = useNames();
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [members, setMembers] = useState<string[]>([]);
  const s = useSubmit(onDone);
  const save = () => {
    if (!name.trim()) { s.setError(new Error('Name the group.')); return; }
    void s.run('/v1/agent-groups', { name: name.trim(), purpose: purpose.trim() || undefined, agent_ids: members }, 'Group created');
  };
  return (
    <ConfirmDialog open title="Create a group" confirmLabel="Propose the group" busy={s.busy} onConfirm={save} onCancel={onClose}>
      <p className="muted small" style={{ margin: 0 }}>A group is a change set: a person signs it before it exists.</p>
      <ErrorBanner error={s.error} />
      <Field label="Name" required><input className={fieldClass} value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field label="What it is for" hint="Optional. Shown on the group."><input className={fieldClass} value={purpose} onChange={(e) => setPurpose(e.target.value)} /></Field>
      <AgentPicker agents={names.agents} chosen={members} onChange={setMembers} legend="Members" />
    </ConfirmDialog>
  );
}

interface Subscription { id: string; topic: string; target_type: string; target_id: string; target_name: string | null; mode: string; work_class: string | null; include_own: boolean; created_at: string }
interface GroupDetail extends GroupRow {
  member_list: Array<{ agent_id: string; name: string; class: string; status: string; claimed_30d: number; done_30d: number; added_at: string }>;
  subscriptions: Subscription[];
}

export function GroupDetailPage() {
  const { id = '' } = useParams();
  const { me } = useSession();
  const g = useApi<GroupDetail>(`/v1/agent-groups/${id}`, [id]);
  const [dialog, setDialog] = useState<'members' | 'subscribe' | 'archive' | null>(null);
  usePageTitle(g.data?.name ?? 'Group');
  if (g.loading && !g.data) return <Loading label="Loading the group" />;
  if (!g.data) return <div className="stack"><ErrorBanner error={g.error} /><Link to="/agents/groups">Back to groups</Link></div>;
  const d = g.data;
  const total = d.member_list.reduce((n, m) => n + m.claimed_30d, 0);
  const change = canConfigure(me.role) && d.status === 'active';
  return (
    <div className="stack">
      <AgentWorkNav />
      <Breadcrumb items={[{ to: '/agents/groups', label: 'Groups' }, { label: d.name }]} />
      <div className="page-head">
        <div><h1>{d.name}</h1><p>{d.purpose ?? 'Agents that share work.'} Owner: {d.owner_name ?? '–'}.</p></div>
        {change && <div className="row" role="group" aria-label="Group actions">
          <Button type="submit" variant="secondary" onClick={() => setDialog('members')}>Change members</Button>
          <Button type="submit" variant="secondary" onClick={() => setDialog('subscribe')}>Subscribe to a topic</Button>
          <Button type="submit" variant="ghost" onClick={() => setDialog('archive')}>Archive</Button>
        </div>}
      </div>
      <section aria-label="This group now" className="stats">
        <div className={`${cardClass} stat`}><div className="num">{d.members}</div><div className="label">Members</div></div>
        <div className={`${cardClass} stat`}><div className="num">{d.queued}</div><div className="label">Waiting for a member</div></div>
        <div className={`${cardClass} stat`}><div className="num">{d.claimed}</div><div className="label">In a member's hands</div></div>
      </section>
      <section className={cardClass} aria-labelledby="members-h">
        <div className="card-head"><h2 id="members-h">Members and their share of work</h2><span className="faint small">Last 30 days</span></div>
        {!d.member_list.length ? <p className="faint card-pad" style={{ margin: 0 }}>No members. Add agents that can do this work.</p> : (
          <Table label="Group members">
              <caption className="sr-only">Members and how much of the group's work each took</caption>
              <thead><tr><th scope="col">Agent</th><th scope="col">Status</th><th scope="col">Took</th><th scope="col">Finished</th><th scope="col" className="hide-sm">Share</th></tr></thead>
              <tbody>{d.member_list.map((m) => (
                <tr key={m.agent_id}>
                  <td><Link to={`/agents/${encodeURIComponent(m.agent_id)}`}>{m.name}</Link><div className="faint" style={{ textTransform: 'capitalize' }}>{m.class}</div></td>
                  <td className="small">{human(m.status)}</td>
                  <td>{m.claimed_30d}</td><td>{m.done_30d}</td>
                  <td className="small hide-sm">{total ? `${Math.round((m.claimed_30d / total) * 100)}%` : '–'}</td>
                </tr>
              ))}</tbody>
            </Table>
        )}
      </section>
      <section className={cardClass} aria-labelledby="subs-h">
        <div className="card-head"><h2 id="subs-h">What it hears</h2></div>
        {!d.subscriptions.length ? <p className="faint card-pad" style={{ margin: 0 }}>No topics yet. Subscribe the group to one.</p> : (
          <ul className="list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {d.subscriptions.map((s) => <li key={s.id} className="list-item"><div className="item-title"><code>{s.topic}</code></div><div className="item-sub">{s.mode === 'assign' ? 'One member takes each item' : 'Every member is told'}</div></li>)}
          </ul>
        )}
      </section>
      <Feed path={`/v1/agent-groups/${id}/feed`} title="Feed" />
      {dialog === 'members' && <MembersDialog group={d} onClose={() => setDialog(null)} onDone={() => { setDialog(null); void g.reload(); }} />}
      {dialog === 'subscribe' && <SubscribeDialog targetType="group" targetId={d.id} onClose={() => setDialog(null)} onDone={() => { setDialog(null); void g.reload(); }} />}
      {dialog === 'archive' && <ArchiveGroup group={d} onClose={() => setDialog(null)} onDone={() => { setDialog(null); void g.reload(); }} />}
    </div>
  );
}

function MembersDialog({ group, onClose, onDone }: { group: GroupDetail; onClose: () => void; onDone: () => void }) {
  const names = useNames();
  const current = group.member_list.map((m) => m.agent_id);
  const [chosen, setChosen] = useState<string[]>(current);
  const s = useSubmit(onDone);
  const save = async () => {
    const add = chosen.filter((x) => !current.includes(x));
    const remove = current.filter((x) => !chosen.includes(x));
    if (!add.length && !remove.length) { onClose(); return; }
    if (add.length) {
      const out = await s.run(`/v1/agent-groups/${group.id}/members`, { action: 'add', agent_ids: add }, 'Members added', !remove.length);
      if (!out || out.state === 'rejected') return;
    }
    if (remove.length) await s.run(`/v1/agent-groups/${group.id}/members`, { action: 'remove', agent_ids: remove }, 'Members removed');
  };
  return (
    <ConfirmDialog open title={`Members of ${group.name}`} confirmLabel="Propose the change" busy={s.busy} onConfirm={() => void save()} onCancel={onClose}>
      <p className="muted small" style={{ margin: 0 }}>Each change is a change set a person signs.</p>
      <ErrorBanner error={s.error} />
      <AgentPicker agents={names.agents} chosen={chosen} onChange={setChosen} legend="Members" />
    </ConfirmDialog>
  );
}

interface Topic { name: string; description: string; subscribe_scope: string; publish_scope: string | null; subscribers: number; types: string[] }

function SubscribeDialog({ targetType, targetId, onClose, onDone }: { targetType: 'group' | 'agent'; targetId: string; onClose: () => void; onDone: () => void }) {
  const topics = useApi<Topic[]>('/v1/topics');
  const [topic, setTopic] = useState('');
  const [mode, setMode] = useState<'notify' | 'assign'>(targetType === 'group' ? 'assign' : 'notify');
  const s = useSubmit(onDone);
  const t = topics.data?.find((x) => x.name === topic);
  return (
    <ConfirmDialog open title="Subscribe to a topic" confirmLabel="Propose the subscription" busy={s.busy}
      onConfirm={() => (topic ? void s.run('/v1/subscriptions', { topic, target_type: targetType, target_id: targetId, mode }, 'Subscribed') : s.setError(new Error('Choose a topic.')))} onCancel={onClose}>
      <ErrorBanner error={s.error} />
      <Field label="Topic" required>
        <select className={selectClass} value={topic} onChange={(e) => setTopic(e.target.value)}>
          <option value="">Choose a topic…</option>
          {topics.data?.map((x) => <option key={x.name} value={x.name}>{x.name}</option>)}
        </select>
      </Field>
      {t && <p className="small muted" style={{ margin: 0 }}>{t.description} Needs <code>{t.subscribe_scope}</code>.</p>}
      <ToggleGroup label="How it hears" value={mode} onChange={setMode} options={[['assign', targetType === 'group' ? 'One member takes it' : 'Assign the work'], ['notify', targetType === 'group' ? 'Tell every member' : 'Tell it']]} />
    </ConfirmDialog>
  );
}

function ArchiveGroup({ group, onClose, onDone }: { group: GroupDetail; onClose: () => void; onDone: () => void }) {
  const s = useSubmit(onDone);
  return (
    <ConfirmDialog open tone="danger" title={`Archive ${group.name}?`} confirmLabel="Propose archiving" busy={s.busy} onConfirm={() => void s.run(`/v1/agent-groups/${group.id}`, { archive: true }, 'Archived')} onCancel={onClose}>
      <ErrorBanner error={s.error} />
      <p style={{ margin: 0 }}>Once signed, the group stops hearing its topics. Work already queued stays. Nothing is deleted.</p>
    </ConfirmDialog>
  );
}

// ---------------------------------------------------------------------------
// Workflows (ADR 0031): a trigger, then steps that each hand work to a named
// agent; a run hands each step's written result to the next. Patterns:
// data-display/list-view (workflows), data-display/table (runs),
// forms/form-validation (the editor), content-management/modal (each change).
// ---------------------------------------------------------------------------

type RunStatus = 'running' | 'waiting_approval' | 'needs_person' | 'done' | 'finished_early' | 'stopped';
const RUN_LABEL: Record<RunStatus, string> = { running: 'Working', waiting_approval: 'Waiting for approval', needs_person: 'Waiting for a person', done: 'Done', finished_early: 'Finished early', stopped: 'Stopped' };
const RUN_TONE: Record<RunStatus, string> = { running: 'info', waiting_approval: 'warn', needs_person: 'warn', done: 'ok', finished_early: '', stopped: 'danger' };
const OPEN_RUNS: RunStatus[] = ['running', 'waiting_approval', 'needs_person'];
const WORKFLOW_STATUS: Record<string, [string, string]> = { active: ['On', 'ok'], paused: ['Paused', 'warn'], retired: ['Retired', ''] };
interface WorkflowTrigger { kind: 'event' | 'signal'; topic: string | null; signal: string | null; description: string | null; watchers: string[] }
interface WorkflowStep { id: string; title: string; agent_id: string; agent_name: string | null; job: string; role: string }
interface Workflow { workflow_id: string; version: number; running_version: number | null; status: 'active' | 'paused' | 'retired' | null; title: string; description: string; trigger: WorkflowTrigger; steps: WorkflowStep[]; runs: Record<RunStatus, number> }
interface Run { id: string; status: RunStatus; step: number; steps: number; step_title: string | null; agent_name: string | null; reason: string | null; correlation_id: string; created_at: string }

export function Workflows() {
  const { me } = useSession();
  const wf = useApi<Workflow[]>('/v1/workflows');
  const topics = useApi<Topic[]>('/v1/topics');
  const names = useNames();
  const [changing, setChanging] = useState<{ w: Workflow; action: 'pause' | 'resume' | 'retire' } | null>(null);
  const reload = wf.reload;
  useEffect(() => { const t = setInterval(() => void reload(), 10_000); return () => clearInterval(t); }, [reload]);
  const change = canConfigure(me.role);
  return (
    <Page title="Workflows" intro="Each workflow starts on a trigger, then hands the work from one of your agents to the next. A step's result goes on once it is approved."
      action={change && <Link className={buttonClass()} to="/agents/workflows/edit"><Icon name="plus" />New workflow</Link>}>
      {hasSurface(me, 'workflow_designer') && <DesignerRequests template="workflow-designer" workClass="workflow_request" onChanged={() => void reload()}
        title="Ask the workflow designer" intro="Describe what should start it and which agent does what, in order. The designer drafts the workflow from your agents for an admin to sign." />}
      <ErrorBanner error={wf.error} />
      {wf.loading && !wf.data ? <ListSkeleton rows={2} label="Loading workflows" /> : !wf.data?.length ? <Empty title="No workflows">Write one with New workflow, or ask the workflow designer.</Empty> : wf.data.map((w) => {
        const headingId = `wf-${w.workflow_id.replace(/[^a-z0-9-]/gi, '-')}`;
        const [label, tone] = WORKFLOW_STATUS[w.status ?? ''] ?? ['Off', ''];
        const t = w.trigger;
        return (
          <section key={w.workflow_id} className={cardClass} aria-labelledby={headingId}>
            <div className="card-head wrap">
              <div><h2 id={headingId}>{w.title} <span className="faint small">v{w.version}</span></h2><div className="small muted">{w.description}</div></div>
              <div className="row wrap" style={{ gap: 6 }}>
                <Badge tone={badgeTone(tone)}>{label}</Badge>
                {change && w.status !== 'retired' && <div className="row" role="group" aria-label={`Change ${w.title}`} style={{ gap: 6 }}>
                  <Link className={buttonClass({ variant: 'secondary', size: 'sm' })} to={`/agents/workflows/edit?workflow=${encodeURIComponent(w.workflow_id)}`}>Edit<span className="sr-only"> {w.title}</span></Link>
                  {w.status === 'active' ? <Button type="submit" variant="secondary" size="sm" onClick={() => setChanging({ w, action: 'pause' })}>Pause</Button> : <Button type="submit" variant="secondary" size="sm" onClick={() => setChanging({ w, action: 'resume' })}>Turn on</Button>}
                  <Button type="submit" variant="ghost" size="sm" onClick={() => setChanging({ w, action: 'retire' })}>Retire</Button>
                </div>}
              </div>
            </div>
            <div className="card-pad stack" style={{ gap: 12 }}>
              <div>
                <h3 className="small" style={{ margin: '0 0 4px' }}>Starts when</h3>
                {t.kind === 'event'
                  ? <p style={{ margin: 0 }}>{topics.data?.find((x) => x.name === t.topic)?.description ?? 'An event happens.'} <code>{t.topic}</code></p>
                  : <p style={{ margin: 0 }}>An agent spots this: {t.description} <span className="faint">Watched by {t.watchers.map((id) => names.agent(id)).join(', ')}.</span></p>}
              </div>
              <div>
                <h3 className="small" style={{ margin: '0 0 6px' }}>Steps</h3>
                <ol className="timeline">
                  {w.steps.map((st) => <li key={st.id}><strong>{st.title}</strong><div className="faint">{st.agent_name ?? st.agent_id}: {jobName(st.job)}</div><div className="small">{st.role}</div></li>)}
                </ol>
              </div>
              <WorkflowRuns w={w} change={change} onChanged={() => void reload()} />
            </div>
          </section>
        );
      })}
      {changing && <ChangeWorkflow {...changing} onClose={() => setChanging(null)} onDone={() => { setChanging(null); void wf.reload(); }} />}
    </Page>
  );
}

// The runs of one workflow, opened on request: where each is, and a person's choice where one waits for them.
function WorkflowRuns({ w, change, onChanged }: { w: Workflow; change: boolean; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const runs = useApi<Run[]>(open ? `/v1/workflow-runs?workflow_id=${encodeURIComponent(w.workflow_id)}` : null, [open, w.runs]);
  const [deciding, setDeciding] = useState<{ run: Run; action: 'continue' | 'stop' } | null>(null);
  const id = `${w.workflow_id.replace(/[^a-z0-9-]/gi, '-')}-runs`;
  const counts = (Object.keys(RUN_LABEL) as RunStatus[]).filter((s) => w.runs[s]).map((s) => `${w.runs[s]} ${RUN_LABEL[s].toLowerCase()}`);
  return (
    <div>
      <div className="row wrap" style={{ gap: 8, justifyContent: 'space-between' }}>
        <span className="small">{counts.length ? counts.join(' · ') : 'No runs yet.'}</span>
        {counts.length > 0 && <Button type="button" variant="ghost" size="sm" className="disclosure self-start pl-0" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Show'} the runs<span className="sr-only"> of {w.title}</span></Button>}
      </div>
      <div id={id} hidden={!open}>
        {open && (runs.loading && !runs.data ? <ListSkeleton rows={2} label="Loading runs" /> : (
          <Table label={`Runs of ${w.title}`} style={{ marginTop: 8 }}>
              <caption className="sr-only">Runs of {w.title}, newest first</caption>
              <thead><tr><th scope="col">Started</th><th scope="col">Status</th><th scope="col">Where</th><th scope="col" className="hide-sm">Why</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>{(runs.data ?? []).map((r) => (
                <tr key={r.id}>
                  <td className="small"><Time iso={r.created_at} /></td>
                  <td><Badge tone={badgeTone(RUN_TONE[r.status])}>{RUN_LABEL[r.status]}</Badge></td>
                  <td className="small">{OPEN_RUNS.includes(r.status) ? `Step ${r.step} of ${r.steps}: ${r.step_title ?? ''}${r.agent_name ? ` (${r.agent_name})` : ''}` : `Ended at step ${r.step} of ${r.steps}`}</td>
                  <td className="small hide-sm">{r.reason ?? <span className="faint">–</span>}</td>
                  <td><div className="row" style={{ gap: 6 }}>
                    <Link className="small" to={`/agents/traces/${r.correlation_id}`}>See the trace</Link>
                    {change && r.status === 'needs_person' && <Button type="submit" variant="secondary" size="sm" onClick={() => setDeciding({ run: r, action: 'continue' })}>Continue</Button>}
                    {change && OPEN_RUNS.includes(r.status) && <Button type="submit" variant="ghost" size="sm" onClick={() => setDeciding({ run: r, action: 'stop' })}>Stop</Button>}
                  </div></td>
                </tr>
              ))}</tbody>
            </Table>
        ))}
      </div>
      {deciding && <DecideRun {...deciding} onClose={() => setDeciding(null)} onDone={() => { setDeciding(null); void runs.reload(); onChanged(); }} />}
    </div>
  );
}

function DecideRun({ run, action, onClose, onDone }: { run: Run; action: 'continue' | 'stop'; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const go = async () => {
    setBusy(true); setError(null);
    try {
      await api(`/v1/workflow-runs/${run.id}/${action}`, { body: action === 'continue' && note.trim() ? { note: note.trim() } : {} });
      toast(action === 'continue' ? 'Handed on to the next step' : 'Run stopped', 'ok');
      onDone();
    } catch (e) { setError(e); } finally { setBusy(false); }
  };
  return (
    <ConfirmDialog open tone={action === 'stop' ? 'danger' : 'primary'} title={action === 'continue' ? 'Continue the run' : 'Stop the run'} confirmLabel={action === 'continue' ? 'Continue' : 'Stop the run'}
      busy={busy} onConfirm={() => void go()} onCancel={onClose}>
      <ErrorBanner error={error} />
      {action === 'continue' ? <>
        <p style={{ margin: 0 }}>The agent at step {run.step} handed this to a person{run.reason ? `: ${run.reason}` : '.'} Continuing hands the step on to the next one{run.step >= run.steps ? ', and as the last step, finishes the run' : ''}.</p>
        <Field label="A note for the next agent" hint="Optional. It travels with the step's result."><textarea className={textareaClass} rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </> : <p style={{ margin: 0 }}>The run ends here. What earlier steps wrote stays, and a task its agent has not started is cancelled.</p>}
    </ConfirmDialog>
  );
}

function ChangeWorkflow({ w, action, onClose, onDone }: { w: Workflow; action: 'pause' | 'resume' | 'retire'; onClose: () => void; onDone: () => void }) {
  const s = useSubmit(onDone);
  const what = { pause: 'Pause', resume: 'Turn on', retire: 'Retire' }[action];
  return (
    <ConfirmDialog open tone={action === 'retire' ? 'danger' : 'primary'} title={`${what} ${w.title}?`} confirmLabel={`Propose: ${what.toLowerCase()}`} busy={s.busy}
      onConfirm={() => void s.run('/v1/workflows/status', { workflow_id: w.workflow_id, action }, `${what} proposed`)} onCancel={onClose}>
      <ErrorBanner error={s.error} />
      <p style={{ margin: 0 }}>{action === 'resume' ? 'Once signed, its trigger starts runs again.' : 'Once signed, its trigger starts no new runs. Runs already going finish.'}</p>
    </ConfirmDialog>
  );
}

// Work no workflow hands out: a consult goes to a domain's owner, only the dedup
// sweeper sends pairs, a conversation turn comes from a person, a browser session
// opens when a person approves it, and the interviewer hands over a setup area.
const NOT_HANDED_OUT = new Set(['consult', 'dedup_pair', 'mcp_turn', 'browser_session_open', 'onboarding_area']);
const jobsOf = (a: AgentRow | undefined) => [...new Set((a?.triggers ?? []).map((t) => String(t.class ?? '')).filter((c) => /^[a-z_]+$/.test(c) && !NOT_HANDED_OUT.has(c)))];
// "Billing question" names the signal billing_question.
const signalOf = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^(\d)/, 's_$1').slice(0, 40);

interface StepDraft { key: number; id: string | null; title: string; agent_id: string; job: string; role: string }

const stepIdOf = (title: string, taken: Set<string>) => {
  // At most 30, so a step_ prefix and a _N suffix stay within the schema's 40.
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30);
  const base = /^[a-z]/.test(slug) ? slug : `step_${slug}`.replace(/_+$/, '');
  let id = base;
  for (let n = 2; taken.has(id); n += 1) id = `${base}_${n}`;
  taken.add(id);
  return id;
};

export function WorkflowEdit() {
  const [params] = useSearchParams();
  const editing = params.get('workflow');
  const wf = useApi<Workflow[]>('/v1/workflows');
  const topics = useApi<Topic[]>('/v1/topics');
  const from = editing ? wf.data?.find((w) => w.workflow_id === editing) ?? null : null;
  usePageTitle(from ? `Edit ${from.title}` : 'New workflow');
  if ((wf.loading && !wf.data) || (topics.loading && !topics.data)) return <Loading label="Loading the workflow" />;
  if (editing && !from) return <div className="stack"><ErrorBanner error={wf.error ?? new Error('No such workflow')} /><Link to="/agents/workflows">Back to workflows</Link></div>;
  return <WorkflowForm key={from ? `${from.workflow_id}@${from.version}` : 'new'} from={from} topics={topics.data ?? []} />;
}

function WorkflowForm({ from, topics }: { from: Workflow | null; topics: Topic[] }) {
  const navigate = useNavigate();
  const names = useNames();
  const hired = names.agents.filter((a) => a.status !== 'retired');
  const next = useRef(0);
  const draft = (st?: WorkflowStep): StepDraft => ({ key: next.current++, id: st?.id ?? null, title: st?.title ?? '', agent_id: st?.agent_id ?? '', job: st?.job ?? '', role: st?.role ?? '' });
  const [title, setTitle] = useState(from?.title ?? '');
  const [description, setDescription] = useState(from?.description ?? '');
  const [kind, setKind] = useState<'event' | 'signal'>(from?.trigger.kind ?? 'event');
  const [topic, setTopic] = useState(from?.trigger.topic ?? '');
  const [signalName, setSignalName] = useState(from?.trigger.signal ? human(from.trigger.signal) : '');
  const [lookFor, setLookFor] = useState(from?.trigger.description ?? '');
  const [watchers, setWatchers] = useState<string[]>(from?.trigger.watchers ?? []);
  const [steps, setSteps] = useState<StepDraft[]>(() => (from ? from.steps.map((st) => draft(st)) : [draft()]));
  const s = useSubmit(() => navigate('/agents/workflows'));
  const change = (key: number, patch: Partial<StepDraft>) => setSteps((all) => all.map((st) => (st.key === key ? { ...st, ...patch } : st)));
  const move = (i: number, by: -1 | 1) => setSteps((all) => { const out = [...all]; [out[i], out[i + by]] = [out[i + by]!, out[i]!]; return out; });
  // Choosing an agent offers its jobs; one job is chosen for you.
  const pickAgent = (key: number, agentId: string) => { const jobs = jobsOf(hired.find((a) => a.id === agentId)); change(key, { agent_id: agentId, job: jobs.length === 1 ? jobs[0]! : '' }); };

  const save = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) { s.setError(new Error('Name the workflow and say what it is for.')); return; }
    if (kind === 'event' && !topic) { s.setError(new Error('Choose what starts it.')); return; }
    if (kind === 'signal' && (!signalOf(signalName) || !lookFor.trim() || !watchers.length)) { s.setError(new Error('Say what the agent looks for, give it a short name, and choose at least one agent to watch for it.')); return; }
    const blank = steps.findIndex((st) => !st.title.trim() || !st.agent_id || !st.job || !st.role.trim());
    if (blank >= 0) { s.setError(new Error(`Step ${blank + 1} needs a name, an agent, its job, and what it does.`)); return; }
    const taken = new Set(steps.flatMap((st) => (st.id ? [st.id] : [])));
    const body = {
      ...(from ? { workflow_id: from.workflow_id, edits_version: from.version } : {}),
      title: title.trim(), description: description.trim(),
      trigger: kind === 'event' ? { kind, topic } : { kind, signal: signalOf(signalName), description: lookFor.trim(), watchers },
      steps: steps.map((st) => ({ id: st.id ?? stepIdOf(st.title, taken), title: st.title.trim(), agent_id: st.agent_id, job: st.job, role: st.role.trim() })),
    };
    void s.run('/v1/workflows', body, from ? 'Edit proposed' : 'Workflow proposed');
  };
  return (
    <div className="stack">
      <AgentWorkNav />
      <Breadcrumb items={[{ to: '/agents/workflows', label: 'Workflows' }, { label: from ? `Edit ${from.title}` : 'New workflow' }]} />
      <div className="page-head"><div><h1>{from ? `Edit ${from.title}` : 'New workflow'}</h1>
        <p>{from ? `Saving proposes version ${from.version + 1}. Runs already going finish on the version they started on.` : 'Choose what starts it, then the agents that take the work, in order.'} An admin signs it before it runs.</p></div></div>
      <form className="stack" onSubmit={save} noValidate>
        <section className={`${cardClass} card-pad stack`} aria-labelledby="wf-about">
          <h2 id="wf-about">About it</h2>
          <Field label="Name" required><input className={fieldClass} value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} /></Field>
          <Field label="What it is for" required><textarea className={textareaClass} rows={2} value={description} maxLength={1000} onChange={(e) => setDescription(e.target.value)} /></Field>
        </section>
        <section className={`${cardClass} card-pad stack`} aria-labelledby="wf-trigger">
          <h2 id="wf-trigger">What starts it</h2>
          <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0, gap: 6 }}>
            <legend className="sr-only">What starts it</legend>
            <label className="row small"><input type="radio" name="wf-kind" checked={kind === 'event'} onChange={() => setKind('event')} />Something happens in your records</label>
            <label className="row small"><input type="radio" name="wf-kind" checked={kind === 'signal'} onChange={() => setKind('signal')} />One of your agents spots something</label>
          </fieldset>
          {kind === 'event' ? (
            <Field label="What happens" required hint="For example a new lead comes in, or a deal moves to another stage.">
              <select className={selectClass} value={topic} onChange={(e) => setTopic(e.target.value)}>
                <option value="">Choose what starts it…</option>
                {topics.map((t) => <option key={t.name} value={t.name}>{t.description} ({t.name})</option>)}
              </select>
            </Field>
          ) : <>
            <Field label="What the agent looks for" required hint="Describe it plainly, for example: a customer asks about billing, such as an invoice, a charge, or a refund.">
              <textarea className={textareaClass} rows={2} maxLength={500} value={lookFor} onChange={(e) => setLookFor(e.target.value)} />
            </Field>
            <Field label="A short name for it" required hint="Shown on signals and runs, for example: Billing question.">
              <input className={fieldClass} maxLength={60} value={signalName} onChange={(e) => setSignalName(e.target.value)} />
            </Field>
            <AgentPicker agents={hired} chosen={watchers} onChange={setWatchers} legend="Agents that watch for it" />
          </>}
        </section>
        <section className={`${cardClass} card-pad stack`} aria-labelledby="wf-steps">
          <h2 id="wf-steps">Steps</h2>
          <p className="faint small" style={{ margin: 0 }}>Each step's agent gets the job once the step before it is approved, with every result so far.</p>
          <ol className="stack" aria-label="Steps in order" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {steps.map((st, i) => {
              const jobs = jobsOf(hired.find((a) => a.id === st.agent_id));
              return (
                <li key={st.key}>
                  <fieldset className="stack" style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, margin: 0 }}>
                    <legend className="small"><strong>Step {i + 1}</strong></legend>
                    <Field label="Name" required><input className={fieldClass} value={st.title} maxLength={120} onChange={(e) => change(st.key, { title: e.target.value })} /></Field>
                    <Field label="Agent" required>
                      <select className={selectClass} value={st.agent_id} onChange={(e) => pickAgent(st.key, e.target.value)}>
                        <option value="">Choose an agent…</option>
                        {st.agent_id && !hired.some((a) => a.id === st.agent_id) && <option value={st.agent_id}>{names.agent(st.agent_id)} (no longer hired)</option>}
                        {hired.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </Field>
                    <Field label="Its job" required hint={st.agent_id && !jobs.length ? 'This agent takes no job a workflow can hand it.' : 'The work this agent does at this step.'}>
                      <select className={selectClass} value={st.job} disabled={!st.agent_id} onChange={(e) => change(st.key, { job: e.target.value })}>
                        <option value="">{st.agent_id ? 'Choose its job…' : 'Choose an agent first'}</option>
                        {st.job && !jobs.includes(st.job) && <option value={st.job}>{jobName(st.job)} (this agent does not do this)</option>}
                        {jobs.map((c) => <option key={c} value={c}>{jobName(c)}</option>)}
                      </select>
                    </Field>
                    <Field label="What it does" required help={'One sentence for the people who read this workflow, such as "Writes the first reply to a new lead." It is a label: how the work gets done comes from the agent\'s own playbook.'}>
                      <input className={fieldClass} value={st.role} maxLength={500} onChange={(e) => change(st.key, { role: e.target.value })} />
                    </Field>
                    <div className="row" role="group" aria-label={`Arrange step ${i + 1}`} style={{ gap: 6 }}>
                      <Button type="button" variant="secondary" size="sm" disabled={i === 0} onClick={() => move(i, -1)}>Move up<span className="sr-only"> step {i + 1}</span></Button>
                      <Button type="button" variant="secondary" size="sm" disabled={i === steps.length - 1} onClick={() => move(i, 1)}>Move down<span className="sr-only"> step {i + 1}</span></Button>
                      <Button type="button" variant="ghost" size="sm" disabled={steps.length === 1} onClick={() => setSteps((all) => all.filter((x) => x.key !== st.key))}>Remove<span className="sr-only"> step {i + 1}</span></Button>
                    </div>
                  </fieldset>
                </li>
              );
            })}
          </ol>
          <div><Button type="button" variant="secondary" disabled={steps.length >= 20} onClick={() => setSteps((all) => [...all, draft()])}><Icon name="plus" />Add a step</Button></div>
        </section>
        <ErrorBanner error={s.error} />
        <div className="row" style={{ gap: 8 }}>
          <Button type="submit" disabled={s.busy}>{from ? 'Propose the new version' : 'Propose the workflow'}</Button>
          <Link className={buttonClass({ variant: 'ghost' })} to="/agents/workflows">Cancel</Link>
        </div>
      </form>
    </div>
  );
}

function TargetSelect({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  const names = useNames();
  return (
    <Field label={label} required>
      <select className={selectClass} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose a group or an agent…</option>
        <optgroup label="Groups">{names.groups.filter((g) => g.status === 'active').map((g) => <option key={g.id} value={`group:${g.id}`}>{g.name}</option>)}</optgroup>
        <optgroup label="Agents">{names.agents.filter((a) => a.status !== 'retired').map((a) => <option key={a.id} value={`agent:${a.id}`}>{a.name}</option>)}</optgroup>
      </select>
    </Field>
  );
}

// ---------------------------------------------------------------------------
// Signals
// ---------------------------------------------------------------------------

interface Ref { entity: string; id: string }
interface Signal { id: string; type: string; subjects: Ref[]; severity: string; evidence: Ref[]; note: string | null; status: string; raised_by: string; raise_count: number; last_raised_at: string; heard_by: number; correlation_id: string | null; resolution: string | null }
const SEVERITY_TONE: Record<string, string> = { high: 'danger', medium: 'warn', low: 'info' };

function RefLink({ r }: { r: Ref }) {
  const to = r.entity === 'party' ? `/contacts/${r.id}` : r.entity === 'deal' ? `/deals/${r.id}` : r.entity === 'quote' ? `/quotes/${r.id}` : null;
  const label = `${human(r.entity)} ${r.id.slice(0, 8)}`;
  return to ? <Link to={to}>{label}</Link> : <span>{label}</span>;
}

export function Signals() {
  const [status, setStatus] = useState<'open' | 'all'>('open');
  const signals = useApi<Signal[]>(`/v1/signals${status === 'all' ? '' : '?status=raised'}`, [status]);
  const topics = useApi<Topic[]>('/v1/topics');
  const names = useNames();
  const [deciding, setDeciding] = useState<Signal | null>(null);
  return (
    <Page title="Signals" intro={SECTION_HELP.signals}>
      <ErrorBanner error={signals.error} />
      <section className={cardClass} aria-labelledby="sig-h">
        <div className="card-head wrap"><h2 id="sig-h">Signals</h2><ToggleGroup label="Which signals" value={status} onChange={setStatus} options={[['open', 'Open'], ['all', 'All']]} /></div>
        {signals.loading && !signals.data ? <ListSkeleton rows={3} label="Loading signals" /> : !signals.data?.length ? (
          <Empty title="Nothing raised">When a detector such as Churn watch or Fraud watch notices something, it shows here.</Empty>
        ) : (
          <ul className="list" style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Signals">
            {signals.data.map((s) => (
              <li key={s.id} className="list-item">
                <div className="row wrap" style={{ justifyContent: 'space-between' }}>
                  <div>
                    <div className="item-title">{human(s.type)} <Badge tone={badgeTone(SEVERITY_TONE[s.severity] ?? '')}>{human(s.severity)}</Badge> <Badge>{human(s.status)}</Badge></div>
                    <div className="item-sub">About {s.subjects.map((r, i) => <span key={i}>{i ? ', ' : ''}<RefLink r={r} /></span>)} · raised by {names.actor(s.raised_by)}{s.raise_count > 1 ? ` ${s.raise_count} times` : ''} · {s.heard_by} told · <Time iso={s.last_raised_at} /></div>
                    {s.note && <p className="small" style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{s.note}</p>}
                    {s.correlation_id && <Link className="small" to={`/agents/traces/${s.correlation_id}`}>See the trace</Link>}
                  </div>
                  {['raised', 'acknowledged'].includes(s.status) && <Button type="submit" variant="secondary" size="sm" onClick={() => setDeciding(s)}>Decide</Button>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className={cardClass} aria-labelledby="topics-h">
        <div className="card-head"><h2 id="topics-h">Topics</h2></div>
        <Table label="Topics">
            <caption className="sr-only">Topics events are published on</caption>
            <thead><tr><th scope="col">Topic</th><th scope="col">Listening</th><th scope="col" className="hide-sm">To hear</th><th scope="col" className="hide-sm">To raise</th></tr></thead>
            <tbody>{topics.data?.map((t) => (
              <tr key={t.name}><td><code>{t.name}</code><div className="faint">{t.description}</div></td><td>{t.subscribers}</td><td className="small hide-sm"><code>{t.subscribe_scope}</code></td><td className="small hide-sm">{t.publish_scope ? <code>{t.publish_scope}</code> : '–'}</td></tr>
            ))}</tbody>
          </Table>
      </section>
      {deciding && <DecideSignal s={deciding} onClose={() => setDeciding(null)} onDone={() => { setDeciding(null); void signals.reload(); }} />}
    </Page>
  );
}

function DecideSignal({ s, onClose, onDone }: { s: Signal; onClose: () => void; onDone: () => void }) {
  const [decision, setDecision] = useState<'acknowledged' | 'resolved' | 'dismissed'>('acknowledged');
  const [resolution, setResolution] = useState('');
  const sub = useSubmit(onDone);
  return (
    <ConfirmDialog open title={`Decide: ${human(s.type)}`} confirmLabel="Save the decision" busy={sub.busy}
      onConfirm={() => void sub.run(`/v1/signals/${s.id}/decide`, { decision, resolution: resolution || undefined }, 'Decided')} onCancel={onClose}>
      <ErrorBanner error={sub.error} />
      <ToggleGroup label="Decision" value={decision} onChange={setDecision} options={[['acknowledged', 'On it'], ['resolved', 'It was real'], ['dismissed', 'Not real']]} />
      <p className="small muted" style={{ margin: 0 }}>Real or not, the answer coaches the detector that raised it.</p>
      <Field label="What happened" hint="Optional."><input className={fieldClass} value={resolution} onChange={(e) => setResolution(e.target.value)} /></Field>
    </ConfirmDialog>
  );
}

// ---------------------------------------------------------------------------
// Consults, handoffs, domains, rulings
// ---------------------------------------------------------------------------

interface Thread { id: string; kind: string; title: string; status: string; domain: string | null; question_kind: string | null; work_class: string | null; from_actor: string; to_type: string; to_id: string; to_name: string | null; subjects: Ref[]; turns: number; max_turns: number | null; deadline: string; correlation_id: string | null; created_at: string; updated_at: string }
interface Ruling { id: string; thread_id: string; domain: string; verdict: string; value: unknown; conditions: string[]; citations: Ref[]; rationale: string; issued_by: string; status: string; overrule_reason: string | null; created_at: string }
interface Domain { domain: string; description: string | null; owner_type: string; owner_id: string; owner_name: string | null; accountable_name: string | null; status: string; open_consults: number; rulings_in_force: number }
const THREAD_TONE: Record<string, string> = { open: 'info', ruled: 'ok', closed: '', expired: 'danger', escalated: 'warn' };

export function Consults() {
  const { me } = useSession();
  const [kind, setKind] = useState<'consult' | 'handoff'>('consult');
  const threads = useApi<Thread[]>(`/v1/agent-threads?kind=${kind}`, [kind]);
  const domains = useApi<Domain[]>('/v1/domains');
  const names = useNames();
  const [owning, setOwning] = useState(false);
  return (
    <Page title="Consults" intro={SECTION_HELP.consults}
      action={canConfigure(me.role) && <Button type="submit" variant="secondary" onClick={() => setOwning(true)}>Name a domain owner</Button>}>
      <ErrorBanner error={threads.error ?? domains.error} />
      <section className={cardClass} aria-labelledby="threads-h">
        <div className="card-head wrap"><h2 id="threads-h">{kind === 'consult' ? 'Consults' : 'Handoffs'}</h2><ToggleGroup label="Kind" value={kind} onChange={setKind} options={[['consult', 'Consults'], ['handoff', 'Handoffs']]} /></div>
        {threads.loading && !threads.data ? <ListSkeleton rows={3} label="Loading threads" /> : !threads.data?.length ? (
          <Empty title={kind === 'consult' ? 'No consults yet' : 'No handoffs yet'}>{kind === 'consult' ? 'Name an owner for a domain; agents can then ask it for rulings.' : 'When an agent hands work to another agent or a group, it shows here.'}</Empty>
        ) : (
          <ul className="list" style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Threads">
            {threads.data.map((t) => (
              <li key={t.id} className="list-item">
                <div className="item-title"><Link to={`/agents/consults/${t.id}`}>{t.title}</Link> <Badge tone={badgeTone(THREAD_TONE[t.status] ?? '')}>{human(t.status)}</Badge></div>
                <div className="item-sub">{names.actor(t.from_actor)} to {t.to_name ?? names.target(t.to_type, t.to_id)}{t.domain ? ` · ${t.domain}` : ''}{t.kind === 'consult' ? ` · turn ${t.turns}${t.max_turns === null ? '' : ` of ${t.max_turns}`}` : ''} · <Time iso={t.updated_at} /></div>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className={cardClass} aria-labelledby="domains-h">
        <div className="card-head"><h2 id="domains-h">Domains and their owners</h2></div>
        {!domains.data?.length ? <p className="faint card-pad" style={{ margin: 0 }}>No domain has an owner yet.</p> : (
          <Table label="Domains">
              <caption className="sr-only">Domains, who rules on each, and who is accountable</caption>
              <thead><tr><th scope="col">Domain</th><th scope="col">Rules on it</th><th scope="col" className="hide-sm">Accountable</th><th scope="col">Open</th><th scope="col">In force</th></tr></thead>
              <tbody>{domains.data.map((d) => (
                <tr key={d.domain}><td>{d.domain}{d.status !== 'active' && <Badge style={{ marginLeft: 6 }}>Retired</Badge>}{d.description && <div className="faint">{d.description}</div>}</td>
                  <td className="small">{d.owner_name ?? d.owner_id}</td><td className="small hide-sm">{d.accountable_name ?? '–'}</td><td>{d.open_consults}</td><td>{d.rulings_in_force}</td></tr>
              ))}</tbody>
            </Table>
        )}
      </section>
      {owning && <DomainOwner onClose={() => setOwning(false)} onDone={() => { setOwning(false); void domains.reload(); }} />}
    </Page>
  );
}

function DomainOwner({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { me } = useSession();
  const users = useApi<Array<{ id: string; display_name: string; status: string }>>('/v1/users');
  const [domain, setDomain] = useState('');
  const [owner, setOwner] = useState('');
  const [accountable, setAccountable] = useState(me.user_id);
  const [description, setDescription] = useState('');
  const s = useSubmit(onDone);
  const save = () => {
    if (!/^[a-z][a-z0-9_]*:[a-z0-9][a-z0-9_.-]*$/.test(domain)) { s.setError(new Error('Name the domain as area:name, for example pricing:discounts.')); return; }
    if (!owner) { s.setError(new Error('Choose who rules on it.')); return; }
    const [t, ...rest] = owner.split(':');
    void s.run('/v1/domains', { domain, owner_type: t, owner_id: rest.join(':'), accountable_user_id: accountable, description: description || undefined }, 'Owner named');
  };
  return (
    <ConfirmDialog open title="Name a domain owner" confirmLabel="Propose the owner" busy={s.busy} onConfirm={save} onCancel={onClose}>
      <ErrorBanner error={s.error} />
      <Field label="Domain" required hint="area:name, for example spec:product-catalog."><input className={fieldClass} value={domain} onChange={(e) => setDomain(e.target.value.trim())} /></Field>
      <Field label="What it covers" hint="Optional."><input className={fieldClass} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
      <TargetSelect label="Rules on it" value={owner} onChange={setOwner} />
      <Field label="Accountable person" required>
        <select className={selectClass} value={accountable} onChange={(e) => setAccountable(e.target.value)}>
          {users.data?.filter((u) => u.status === 'active').map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
        </select>
      </Field>
    </ConfirmDialog>
  );
}

interface ThreadDetail extends Thread { messages: Array<{ n: number; author: string; role: string; kind: string; body: string; proposal_id: string; created_at: string }>; rulings: Ruling[] }
const VERDICT: Record<string, string> = { allowed: 'Allowed', not_allowed: 'Not allowed', conditional: 'Allowed with conditions', value: 'A value' };

export function ConsultDetail() {
  const { id = '' } = useParams();
  const { me } = useSession();
  const t = useApi<ThreadDetail>(`/v1/agent-threads/${id}`, [id]);
  const names = useNames();
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<'answer' | 'clarify' | 'note'>('note');
  const [overruling, setOverruling] = useState<Ruling | null>(null);
  const s = useSubmit(() => { setBody(''); void t.reload(); });
  usePageTitle(t.data?.title ?? 'Consult');
  if (t.loading && !t.data) return <Loading label="Loading the thread" />;
  if (!t.data) return <div className="stack"><ErrorBanner error={t.error} /><Link to="/agents/consults">Back to consults</Link></div>;
  const d = t.data;
  return (
    <div className="stack">
      <AgentWorkNav />
      <Breadcrumb items={[{ to: '/agents/consults', label: 'Consults' }, { label: d.title }]} />
      <div className="page-head">
        <div><h1>{d.title}</h1><p>{names.actor(d.from_actor)} to {d.to_name ?? names.target(d.to_type, d.to_id)}{d.domain ? ` on ${d.domain}` : ''}. <Badge tone={badgeTone(THREAD_TONE[d.status] ?? '')}>{human(d.status)}</Badge></p></div>
        {d.correlation_id && <Link className={buttonClass({ variant: 'secondary' })} to={`/agents/traces/${d.correlation_id}`}>See the trace</Link>}
      </div>
      <section className={cardClass} aria-labelledby="turns-h">
        <div className="card-head"><h2 id="turns-h">Turns</h2>{d.kind === 'consult' && <span className="faint small">{d.turns}{d.max_turns === null ? '' : ` of ${d.max_turns}`}</span>}</div>
        <div className="card-pad">
          {d.subjects.length > 0 && <p className="small" style={{ marginTop: 0 }}>About {d.subjects.map((r, i) => <span key={i}>{i ? ', ' : ''}<RefLink r={r} /></span>)}</p>}
          <ol className="timeline">
            {d.messages.map((m) => (
              <li key={m.n} className={m.kind === 'ruling' ? 'ok' : m.kind === 'overrule' ? 'danger' : undefined}>
                <strong>{names.actor(m.author)}</strong> <span className="faint">({m.role}) · {human(m.kind)} · <Time iso={m.created_at} /></span>
                <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{m.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
      {d.rulings.map((r) => (
        <section key={r.id} className={cardClass} aria-labelledby={`ruling-${r.id}`}>
          <div className="card-head"><h2 id={`ruling-${r.id}`}>Ruling: {VERDICT[r.verdict] ?? r.verdict}</h2><Badge tone={r.status === 'in_force' ? 'success' : 'danger'}>{human(r.status)}</Badge></div>
          <div className="card-pad stack" style={{ gap: 6 }}>
            {r.conditions.length > 0 && <div><strong className="small">Conditions</strong><ul className="small" style={{ margin: 0 }}>{r.conditions.map((c, i) => <li key={i}>{c}</li>)}</ul></div>}
            {r.verdict === 'value' && <div className="small"><strong>Value:</strong> <code>{JSON.stringify(r.value)}</code></div>}
            <p className="small" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{r.rationale}</p>
            <div className="small">Relied on {r.citations.map((c, i) => <span key={i}>{i ? ', ' : ''}<RefLink r={c} /></span>)}</div>
            {r.overrule_reason && <div className="small">Overruled: {r.overrule_reason}</div>}
            {r.status === 'in_force' && isAdmin(me) && <div><Button type="submit" variant="ghost" size="sm" onClick={() => setOverruling(r)}>Overrule</Button></div>}
          </div>
        </section>
      ))}
      {d.kind === 'consult' && d.status === 'open' && (
        <section className={cardClass} aria-labelledby="join-h">
          <div className="card-head"><h2 id="join-h">Join in</h2></div>
          <form className="card-pad stack" onSubmit={(e) => { e.preventDefault(); if (body.trim()) void s.run(`/v1/agent-threads/${id}/reply`, { kind, body: body.trim() }, 'Added'); }}>
            <ErrorBanner error={s.error} />
            <ToggleGroup label="What this is" value={kind} onChange={setKind} options={[['note', 'A note'], ['answer', 'An answer'], ['clarify', 'A question']]} />
            <Field label="Your turn"><textarea className={textareaClass} rows={3} value={body} onChange={(e) => setBody(e.target.value)} /></Field>
            <div><Button type="submit" disabled={s.busy || !body.trim()}>Add your turn</Button></div>
          </form>
        </section>
      )}
      {overruling && <Overrule r={overruling} onClose={() => setOverruling(null)} onDone={() => { setOverruling(null); void t.reload(); }} />}
    </div>
  );
}

function Overrule({ r, onClose, onDone }: { r: Ruling; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const s = useSubmit(onDone);
  return (
    <ConfirmDialog open tone="danger" title="Overrule this ruling?" confirmLabel="Overrule" busy={s.busy}
      onConfirm={() => (reason.trim() ? void s.run(`/v1/rulings/${r.id}/overrule`, { reason: reason.trim() }, 'Overruled') : s.setError(new Error('Say why.')))} onCancel={onClose}>
      <ErrorBanner error={s.error} />
      <p style={{ margin: 0 }}>The ruling stays on record as overruled. Proposals can no longer rely on it.</p>
      <Field label="Why" required><input className={fieldClass} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
    </ConfirmDialog>
  );
}

// ---------------------------------------------------------------------------
// The trace and feeds
// ---------------------------------------------------------------------------

interface TraceItem { seq: string; at: string; type: string; actor: string; kind: string; summary: string; table: string | null; row_id: string | null; proposal_id: string | null; run_id: string | null; refs: Record<string, string | null>; correlation_id?: string | null }
const KIND_LABEL: Record<string, string> = { delivery: 'Delivered', run: 'Run', proposal: 'Proposal', decision: 'Gate', signal: 'Signal', handoff: 'Handoff', consult: 'Consult', ruling: 'Ruling', thread: 'Thread', message: 'Turn', exception: 'Needs you', approval: 'Approval', record: 'Record', event: 'Event', ledger: 'Ledger' };
const KIND_TONE: Record<string, string> = { ruling: 'ok', signal: 'warn', exception: 'danger', decision: 'info', handoff: 'accent', consult: 'accent' };

function ItemLinks({ i }: { i: TraceItem }) {
  const thread = i.refs.thread_id;
  return (
    <span className="small">
      {i.proposal_id && <> · <Link to={`/activity/${i.proposal_id}`}>Proposal</Link></>}
      {thread && <> · <Link to={`/agents/consults/${thread}`}>Thread</Link></>}
    </span>
  );
}

export function Traces() {
  const traces = useApi<Array<{ correlation_id: string; started_at: string; last_at: string; events: number; agents: string[]; first_type: string }>>('/v1/traces');
  const names = useNames();
  return (
    <Page title="Traces" intro={SECTION_HELP.traces}>
      <ErrorBanner error={traces.error} />
      <section className={cardClass} aria-labelledby="traces-h">
        <div className="card-head"><h2 id="traces-h">Recent chains</h2></div>
        {traces.loading && !traces.data ? <ListSkeleton rows={4} label="Loading traces" /> : !traces.data?.length ? <Empty title="No chains yet">When agents start working, each chain of work shows here.</Empty> : (
          <ul className="list" style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Chains of work">
            {traces.data.map((t) => (
              <li key={t.correlation_id} className="list-item">
                <div className="item-title"><Link to={`/agents/traces/${t.correlation_id}`}>{human(t.first_type.replace(/^row\./, '').replace(/\./g, ' '))}</Link></div>
                <div className="item-sub">{t.agents.length ? t.agents.map(names.agent).join(', ') : 'No agent yet'} · {t.events} steps · started <Time iso={t.started_at} />, last <Time iso={t.last_at} /></div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </Page>
  );
}

export function TraceView() {
  const { id = '' } = useParams();
  const t = useApi<{ correlation_id: string; started_at: string | null; agents: string[]; items: TraceItem[] }>(`/v1/traces/${id}`, [id]);
  const names = useNames();
  const [show, setShow] = useState<'work' | 'all'>('work');
  usePageTitle('Trace');
  const reload = t.reload;
  useEffect(() => { const x = setInterval(() => void reload(), 5000); return () => clearInterval(x); }, [reload]);
  if (t.loading && !t.data) return <Loading label="Loading the trace" />;
  if (!t.data) return <div className="stack"><ErrorBanner error={t.error} /><Link to="/agents/traces">Back to traces</Link></div>;
  // The work view leaves out bookkeeping (gate steps and run steps) that the full view keeps.
  const items = show === 'all' ? t.data.items : t.data.items.filter((i) => !['decision', 'record', 'event'].includes(i.kind) && !(i.kind === 'run' && i.table !== 'runs'));
  return (
    <div className="stack">
      <AgentWorkNav />
      <Breadcrumb items={[{ to: '/agents/traces', label: 'Traces' }, { label: 'One chain of work' }]} />
      <div className="page-head">
        <div><h1>One chain of work</h1><p>{t.data.agents.length ? `${t.data.agents.map(names.agent).join(', ')} took part.` : 'No agent took part yet.'} {t.data.started_at && <>Started <Time iso={t.data.started_at} />.</>}</p></div>
        <ToggleGroup label="Show" value={show} onChange={setShow} options={[['work', 'The work'], ['all', 'Every step']]} />
      </div>
      <section className={cardClass} aria-labelledby="trace-h">
        <div className="card-head"><h2 id="trace-h">{items.length} steps</h2></div>
        <div className="card-pad">
          <ol className="timeline" aria-label="Steps in order">
            {items.map((i) => (
              <li key={i.seq} className={KIND_TONE[i.kind] === 'ok' ? 'ok' : KIND_TONE[i.kind] === 'danger' ? 'danger' : KIND_TONE[i.kind] === 'warn' ? 'warn' : undefined}>
                <Badge tone={badgeTone(KIND_TONE[i.kind] ?? '')}>{KIND_LABEL[i.kind] ?? human(i.kind)}</Badge> {i.summary}
                <div className="faint small">{names.actor(i.actor)} · <Time iso={i.at} /> · #{i.seq}<ItemLinks i={i} /></div>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </div>
  );
}

// What an agent or group did and was sent, newest first; each item opens its chain.
export function Feed({ path, title }: { path: string; title: string }) {
  const feed = useApi<TraceItem[]>(`${path}?limit=30`, [path]);
  const names = useNames();
  return (
    <section className={cardClass} aria-labelledby="feed-h">
      <div className="card-head"><h2 id="feed-h">{title}</h2></div>
      <ErrorBanner error={feed.error} />
      {feed.loading && !feed.data ? <ListSkeleton rows={3} label="Loading the feed" /> : !feed.data?.length ? <p className="faint card-pad" style={{ margin: 0 }}>Nothing yet.</p> : (
        <ul className="list" style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label={title}>
          {feed.data.map((i) => (
            <li key={i.seq} className="list-item">
              <div className="item-title"><Badge tone={badgeTone(KIND_TONE[i.kind] ?? '')}>{KIND_LABEL[i.kind] ?? human(i.kind)}</Badge> {i.summary}</div>
              <div className="item-sub">{names.actor(i.actor)} · <Time iso={i.at} />{i.correlation_id && <> · <Link to={`/agents/traces/${i.correlation_id}`}>Trace</Link></>}<ItemLinks i={i} /></div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

